import React from 'react';
import { Card } from './ui/card';
import { 
  Building2, 
  HeartPulse, 
  GraduationCap, 
  ShoppingCart,
  Plane,
  Landmark,
  Factory,
  Truck,
  Car,
  Home,
  Lightbulb,
  Music
} from 'lucide-react';

const Industries = () => {
  const industries = [
    {
      icon: HeartPulse,
      title: 'Healthcare & Pharma',
      description: 'Digital health solutions, telemedicine platforms, patient management systems, and AI-powered diagnostics.',
      color: 'bg-red-100 text-red-600',
      gradient: 'from-red-500 to-pink-500'
    },
    {
      icon: Landmark,
      title: 'Banking & Finance',
      description: 'Fintech solutions, digital banking, payment gateways, blockchain, and regulatory compliance systems.',
      color: 'bg-blue-100 text-blue-600',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      icon: ShoppingCart,
      title: 'Retail & E-commerce',
      description: 'E-commerce platforms, inventory management, omnichannel solutions, and personalized shopping experiences.',
      color: 'bg-purple-100 text-purple-600',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      icon: GraduationCap,
      title: 'Education & E-learning',
      description: 'Learning management systems, virtual classrooms, student portals, and AI-powered personalized learning.',
      color: 'bg-green-100 text-green-600',
      gradient: 'from-green-500 to-emerald-500'
    },
    {
      icon: Factory,
      title: 'Manufacturing',
      description: 'Industry 4.0 solutions, IoT integration, predictive maintenance, and supply chain optimization.',
      color: 'bg-orange-100 text-orange-600',
      gradient: 'from-orange-500 to-red-500'
    },
    {
      icon: Truck,
      title: 'Logistics & Transportation',
      description: 'Fleet management, route optimization, real-time tracking, and warehouse automation solutions.',
      color: 'bg-indigo-100 text-indigo-600',
      gradient: 'from-indigo-500 to-purple-500'
    },
    {
      icon: Building2,
      title: 'Real Estate & Construction',
      description: 'Property management, smart building solutions, project management, and virtual property tours.',
      color: 'bg-cyan-100 text-cyan-600',
      gradient: 'from-cyan-500 to-blue-500'
    },
    {
      icon: Plane,
      title: 'Travel & Hospitality',
      description: 'Booking systems, customer management, loyalty programs, and AI-powered recommendations.',
      color: 'bg-teal-100 text-teal-600',
      gradient: 'from-teal-500 to-green-500'
    },
    {
      icon: Lightbulb,
      title: 'Energy & Utilities',
      description: 'Smart grid solutions, energy management, renewable energy platforms, and consumption analytics.',
      color: 'bg-yellow-100 text-yellow-600',
      gradient: 'from-yellow-500 to-orange-500'
    },
    {
      icon: Music,
      title: 'Media & Entertainment',
      description: 'Content management, streaming platforms, digital rights management, and audience analytics.',
      color: 'bg-pink-100 text-pink-600',
      gradient: 'from-pink-500 to-rose-500'
    },
    {
      icon: Car,
      title: 'Automotive',
      description: 'Connected vehicles, autonomous driving systems, fleet management, and automotive IoT solutions.',
      color: 'bg-gray-100 text-gray-600',
      gradient: 'from-gray-500 to-slate-500'
    },
    {
      icon: Home,
      title: 'Telecom & Communications',
      description: '5G solutions, network optimization, customer portals, and unified communication platforms.',
      color: 'bg-violet-100 text-violet-600',
      gradient: 'from-violet-500 to-purple-500'
    }
  ];

  return (
    <section id="industries" className="py-20 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Section Title */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Industries We Serve
          </h2>
          <div className="w-20 h-1 bg-cyan-600 mx-auto mb-6"></div>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Delivering tailored technology solutions across diverse industries to drive digital transformation and business growth
          </p>
        </div>

        {/* Industries Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {industries.map((industry) => {
            const Icon = industry.icon;
            return (
              <Card
                key={industry.title}
                className="bg-white p-8 rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-0 group cursor-pointer"
              >
                <div className={`w-16 h-16 ${industry.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <Icon className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {industry.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {industry.description}
                </p>
              </Card>
            );
          })}
        </div>

        {/* Bottom Section */}
        <div className="mt-16 text-center bg-gradient-to-r from-cyan-50 to-blue-50 p-8 rounded-2xl">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Your Industry Not Listed?
          </h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            We work with businesses across all sectors. Our adaptable solutions can be customized to meet the unique needs of any industry.
          </p>
          <a
            href="#contact"
            className="inline-block bg-cyan-600 hover:bg-cyan-700 text-white px-8 py-3 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            Discuss Your Requirements
          </a>
        </div>
      </div>
    </section>
  );
};

export default Industries;
